import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  login as apiLogin,
  logout as apiLogout,
  tryRefreshToken,
  getCurrentUser,
  type CurrentUser,
} from "@/services/authService";
import { loginToScBackend } from "@/services/authService";
import { loginWithFaceToScBackend } from "@/services/faceAuth";
import {
  removeAllTokens,
  ensureFreshAccessToken,
  SESSION_EXPIRED_EVENT,
} from "@/services/api";
import {
  TEST_MODE_DEFAULT_LOGIN,
  TEST_MODE_DEFAULT_PASSWORD,
} from "@/constants/testAuth";
import { dashboardProfileMock } from "@/data/dashboardProfileMockData";

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  middleName: string;
  fullName: string;
  organizationId: string;
  positionId: string;
  positionName: string;
  language: string;
  canAuthorize: boolean;
  isAdmin: boolean;
  avatar: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isTokenAuth: boolean; // Авторизован ли через токен (ограниченный доступ)
  isTestModeAuth: boolean; // Авторизован ли через TEST_MODE (автоматическая авторизация)
  login: (positionCode: string, positionPassword: string) => Promise<boolean>;
  /** Вход по логину и кадру с камеры. Бросает `FaceLoginError` — экрану нужна причина. */
  loginWithFace: (username: string, image: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/*
 * Профиль `GET /auth/me` → пользователь интерфейса. Должность и подразделение —
 * из первой записи о работе: у руководителя области она одна, а шапке нужна
 * одна строка. Имени у учётки может не быть (служебные) — тогда логин.
 */
function toUser(me: CurrentUser): User {
  const fullName = (me.fullName ?? "").trim() || me.username;
  const [lastName = "", firstName = "", ...rest] = fullName.split(/\s+/);
  const job = me.employments?.[0];
  return {
    id: String(me.id),
    username: me.username,
    firstName,
    lastName,
    middleName: rest.join(" "),
    fullName,
    organizationId: job ? String(job.orgUnitId) : "",
    positionId: job ? String(job.staffUnitId) : "",
    positionName: job?.staffTitleRu ?? "",
    language: "RUS",
    canAuthorize: true,
    isAdmin: me.isSuperadmin,
    avatar: "",
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenAuth, setIsTokenAuth] = useState(false); // Авторизация по токену
  const [isTestModeAuth, setIsTestModeAuth] = useState(false); // TEST_MODE авторизация

  // Очистка сессии
  const clearSession = useCallback(() => {
    setUser(null);
    setIsTokenAuth(false);
    setIsTestModeAuth(false);
    localStorage.removeItem("auth-user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("positions");
    localStorage.removeItem("isTokenAuth");
    localStorage.removeItem("testModeAuth");
  }, []);

  /*
   * СЕССИЮ БОЛЬШЕ НЕ ПРОДЛИТЬ — ВЫХОДИМ. `api.ts` шлёт событие, когда бэкенд
   * отверг refresh-токен (отозван, истёк, уже предъявлялся) или отвечает 401 на
   * свежий токен. Очистки состояния достаточно: `ProtectedRoute` сам уведёт на
   * форму входа, а она скажет, что сессия истекла. Слушатель стоит раньше
   * восстановления сессии, чтобы не пропустить отказ refresh при старте.
   */
  useEffect(() => {
    const onExpired = () => clearSession();
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [clearSession]);

  // Проверка сохраненной сессии при загрузке
  useEffect(() => {
    const initSession = async () => {
      // Тестовая сессия — только в dev-сборке (см. тестовый вход ниже).
      if (import.meta.env.VITE_TEST_MODE === "true") {
        const savedTestAuth = localStorage.getItem("testModeAuth") === "true";
        if (savedTestAuth) {
          console.log("[Auth] TEST_MODE - restoring test session");
          const testUser: User = {
            id: "test-user",
            username: "SUPPORT_001",
            firstName: dashboardProfileMock.firstName,
            lastName: dashboardProfileMock.lastName,
            middleName: dashboardProfileMock.middleName,
            fullName: dashboardProfileMock.fullName,
            organizationId: "",
            positionId: "",
            positionName: dashboardProfileMock.position.replace("\n", " "),
            language: "RUS",
            canAuthorize: true,
            isAdmin: true,
            avatar: dashboardProfileMock.avatar,
          };
          setUser(testUser);
          setIsTestModeAuth(true);
          return;
        }
        // Если нет сохраненной сессии - покажем страницу логина
        return;
      }

      const savedUser = localStorage.getItem("auth-user");
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      const savedIsTokenAuth = localStorage.getItem("isTokenAuth") === "true";

      if (savedUser && accessToken) {
        try {
          setUser(JSON.parse(savedUser));
          setIsTokenAuth(savedIsTokenAuth);
          if (!savedIsTokenAuth) {
            /*
             * ОБНОВЛЯЕМ, ТОЛЬКО ЕСЛИ ТОКЕН НА ИСХОДЕ. Раньше refresh шёл при
             * каждой перезагрузке страницы, даже со свежим токеном: лишняя
             * ротация и гонка с первыми запросами экрана за один и тот же
             * одноразовый refresh-токен.
             */
            await ensureFreshAccessToken();
          }
        } catch (error) {
          console.error("Error restoring session:", error);
        }
      } else if (refreshToken && !accessToken) {
        await tryRefreshToken();
      }

    };

    initSession();
  }, [clearSession]);

  // Авторизация по токену (ограниченный доступ к одной странице)
  const loginWithToken = useCallback(
    async (token: string): Promise<boolean> => {
      try {
        localStorage.setItem("accessToken", token);
        localStorage.setItem("isTokenAuth", "true");

        // Сначала устанавливаем временного пользователя чтобы не было задержки
        const tempUser: User = {
          id: "",
          username: "token_user",
          firstName: "",
          lastName: "",
          middleName: "",
          fullName: "Загрузка...",
          organizationId: "",
          positionId: "",
          positionName: "",
          language: "RUS",
          canAuthorize: true,
          isAdmin: false,
          avatar: "",
        };
        setUser(tempUser);
        setIsTokenAuth(true);

        // Загружаем реальный профиль пользователя
        try {
          const userData = toUser(await getCurrentUser());
          setUser(userData);
          localStorage.setItem("auth-user", JSON.stringify(userData));
        } catch (profileError) {
          console.warn("Failed to load user profile for token auth:", profileError);
          // Оставляем временного пользователя если не удалось загрузить профиль
          localStorage.setItem("auth-user", JSON.stringify(tempUser));
        }

        return true;
      } catch (error) {
        console.error("Login with token failed:", error);
        return false;
      }
    },
    []
  );

  /*
   * После выдачи пары — общее для пароля и лица.
   *
   * ОТКАЗ ПРОФИЛЯ НЕ ОТМЕНЯЕТ ВХОД. Токен уже получен; падать после него
   * значит выкидывать вошедшего человека обратно на форму. Без профиля в
   * шапке будет логин вместо имени — и только.
   */
  const finishScLogin = useCallback(async (username: string) => {
    const me = await getCurrentUser().catch(() => null);
    const userData = toUser(
      me ?? { id: 0, username, isSuperadmin: false, mustChangePassword: false, employments: [], permissions: [] },
    );

    setUser(userData);
    setIsTokenAuth(false); // Обычная авторизация - полный доступ
    setIsTestModeAuth(false);
    localStorage.setItem("auth-user", JSON.stringify(userData));
    localStorage.removeItem("isTokenAuth");
    localStorage.removeItem("tokenAccessPage");
  }, []);

  /*
   * ВХОД ПО ЛИЦУ — БЕЗ ТЕСТОВОЙ ВЕТКИ. Тестовый вход проверяет пару логин +
   * пароль на клиенте; с лицом так нельзя и незачем — сверку делает только
   * бэкенд (CompreFace), и в тестовом режиме лицо проверяется по-настоящему.
   *
   * Ошибку пробрасываем, а не превращаем в `false`, как `login`: экрану надо
   * отличить «не узнали» от «сервис лёг, войдите паролем».
   */
  const loginWithFace = useCallback(
    async (username: string, image: string): Promise<void> => {
      setIsLoading(true);
      try {
        const u = username.trim();
        await loginWithFaceToScBackend(u, image);
        await finishScLogin(u);
      } catch (error) {
        clearSession();
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [clearSession, finishScLogin],
  );

  // Авторизация (код + пароль)
  const login = useCallback(
    async (positionCode: string, positionPassword: string): Promise<boolean> => {
      setIsLoading(true);
      try {
        /*
         * ТЕСТОВЫЙ ВХОД — ТОЛЬКО В DEV-СБОРКЕ. Проверка через
         * `import.meta.env.VITE_TEST_MODE` прямо в условии, а не через
         * `isTestMode()`: Vite подставляет значение при сборке, в прод-образе это
         * `"false" === "true"`, и минификатор выбрасывает ветку вместе с
         * тестовой парой. Через функцию ветка оставалась в бандле мёртвым
         * кодом, и пароль настоящей учётки `superadmin` лежал там открытым
         * текстом (проверено по `dist` 27.09.2026).
         */
        if (import.meta.env.VITE_TEST_MODE === "true") {
          if (positionCode === TEST_MODE_DEFAULT_LOGIN && positionPassword === TEST_MODE_DEFAULT_PASSWORD) {
            console.log("[Auth] TEST_MODE - credentials accepted");

            /*
             * СРАЗУ БЕРЁМ НАСТОЯЩИЙ ТОКЕН У `sc_atyrau_backend`.
             *
             * Без него вход остаётся декоративным: пользователь внутри, но
             * каждый модуль на живых данных получает 401, потому что подписан
             * токеном от другого продукта.
             *
             * ОТКАЗ НЕ БЛОКИРУЕТ ВХОД, И ЭТО НАМЕРЕННО. Большая часть планшета
             * до сих пор на заглушках; уронив вход при недоступном бэкенде, мы
             * сломали бы демо целиком ради модулей, которых пока единицы.
             * Поэтому пишем в консоль явную причину и пускаем на заглушках.
             */
            try {
              await loginToScBackend(positionCode, positionPassword);
              console.log("[Auth] sc_atyrau_backend - токен получен");
            } catch (e) {
              removeAllTokens();
              console.warn(
                "[Auth] sc_atyrau_backend недоступен, живые модули покажут 401:",
                e instanceof Error ? e.message : e,
              );
            }
            const testUser: User = {
              id: "test-user",
              username: "SUPPORT_001",
              firstName: dashboardProfileMock.firstName,
              lastName: dashboardProfileMock.lastName,
              middleName: dashboardProfileMock.middleName,
              fullName: dashboardProfileMock.fullName,
              organizationId: "",
              positionId: "",
              positionName: dashboardProfileMock.position.replace("\n", " "),
              language: "RUS",
              canAuthorize: true,
              isAdmin: true,
              avatar: dashboardProfileMock.avatar,
            };
            setUser(testUser);
            setIsTestModeAuth(true);
            localStorage.setItem("testModeAuth", "true");
            return true;
          } else {
            console.log("[Auth] TEST_MODE - invalid credentials");
            throw new Error("Неверный код доступа или пароль");
          }
        }

        /*
         * ВХОД ПО ЛОГИНУ И ПАРОЛЮ — БЕЗ ФОТО И БЕЗ `deviceInfo`.
         *
         * Бэкенд Атырау принимает ровно два поля и валидирует тело строго
         * (`forbidNonWhitelisted`): на любое лишнее — например, снимок лица,
         * который слал прежний вход, — отвечает 400 при верных логине и пароле.
         */
        await loginToScBackend(positionCode, positionPassword);
        await finishScLogin(positionCode);

        return true;
      } catch (error) {
        console.error("Login failed:", error);
        clearSession();
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [clearSession, finishScLogin]
  );

  // Выход
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiLogout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearSession();
      setIsLoading(false);
    }
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isTokenAuth,
        isTestModeAuth,
        login,
        loginWithFace,
        loginWithToken,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
